import Alert from '@amzn/awsui-components-react/polaris/alert';
import Button from '@amzn/awsui-components-react/polaris/button';
import { Event } from '@amzn/red-velvet-api';
import { useLocalizedText } from '../../hooks/localizedText';
import { AlertInfo } from '../../hooks/betterEvents/reducer';

/** Campaign tag appended to the shared signup link so mailto click-throughs stay attributable. */
const SHARE_MAILTO_VC_CAMPAIGN = 'vcshare-signupsuccessfulalert-mailto';

/** The shift details the Share-via-email body needs; absent on cancel/error so the action is hidden. */
export interface SignupSuccessShiftInfo {
  shiftName?: string;
  shiftDate?: string;
}

/**
 * The signup success/error alert shared by the card view (ShiftCarousel) and the table view
 * (EventVolunteerShiftTable) so the two stay identical: a bold "Success" header, the signup body
 * message, and — only for a create signup (shiftInfo present) — a "Share via email" mailto action.
 * The alert data (type/message/shiftInfo) comes from useSignupAlerts; this component owns only the
 * presentation, including the mailto construction that previously lived inline in ShiftCarousel.
 */
export function SignupSuccessAlert({
  event,
  alert,
  shiftInfo,
  onDismiss,
}: {
  event: Event;
  alert: AlertInfo;
  shiftInfo?: SignupSuccessShiftInfo;
  onDismiss: () => void;
}) {
  const { t } = useLocalizedText(['translation']);
  return (
    <Alert
      dismissible
      statusIconAriaLabel={t('eventDisplay.' + alert.type + 'Title')}
      onDismiss={onDismiss}
      type={alert.type}
      i18nStrings={{
        dismissAriaLabel: t(`alert.dismiss.${alert.type}`),
      }}
      action={
        shiftInfo ? (
          <Button
            iconName="envelope"
            href={`mailto:?subject=${encodeURIComponent(t('useEvents.signupSuccessShareSubject', { eventTitle: event.title }))}&body=${encodeURIComponent(t('useEvents.signupSuccessShareBody', { eventTitle: event.title, shiftName: shiftInfo.shiftName, shiftDate: shiftInfo.shiftDate ?? '', link: `${window.location.origin}/view-event?id=${event.eventId}&vc_campaign=${SHARE_MAILTO_VC_CAMPAIGN}` }))}`}
            data-aci-analytics-name="signupSuccessAlert_shareMailto"
          >
            {t('useEvents.signupSuccessShareMailto')}
          </Button>
        ) : undefined
      }
    >
      <b>{t('eventDisplay.' + alert.type + 'Title')}</b>
      <div>{alert.message}</div>
    </Alert>
  );
}
